import { API_ENDPOINTS } from '@/constants';
import { CreditBalanceResponse, GrantCreditRequest, GrantCreditResponse } from '@/types/credits';
import { RepoAnalyzerResponse } from '@/types/repoAnalyzer';
import { RugWalletResponse } from '@/types/rugWalletResponse';
import {
  SolanaCreatePaymentTxRequest,
  SolanaCreatePaymentTxResponse,
  SolanaSendTransactionRequest,
  SolanaSendTransactionResponse,
} from '@/types/solana';
import { TGMetricsResponse } from '@/types/tgMetrics';
import { TokenInfoResponse } from '@/types/tokenInfoResponse';
import { TrenchRadarResponse } from '@/types/trenchRadar';
import { TrendingTokensResponse } from '@/types/trendingTokens';
import { CreateUserRequest, UserResponse } from '@/types/user';
import { CreateViewRequest, ViewHistoryResponse } from '@/types/view';
import { XSensationResponse } from '@/types/x';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: response.statusText }));
    throw new ApiError(response.status, error.message || response.statusText);
  }
  return response.json();
}

export async function apiClient<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  });

  return handleResponse<T>(response);
}

// API methods
export const api = {
  // Token related
  getTokenInfo: (ca: string) =>
    apiClient<TokenInfoResponse>(API_ENDPOINTS.tokenInfo, {
      method: 'POST',
      body: JSON.stringify({ ca }),
    }),

  getTrendingTokens: () => apiClient<TrendingTokensResponse>(API_ENDPOINTS.getTrendingTokens),

  checkCA: (ca: string) =>
    apiClient<any>(API_ENDPOINTS.checkCA, {
      method: 'POST',
      body: JSON.stringify({ ca }),
    }),

  checkRugWallet: (wallet: string) =>
    apiClient<RugWalletResponse>(API_ENDPOINTS.checkRugWallet, {
      method: 'POST',
      body: JSON.stringify({ wallet }),
    }),

  xSensation: (username: string) =>
    apiClient<XSensationResponse>(API_ENDPOINTS.xSensation, {
      method: 'POST',
      body: JSON.stringify({ username }),
    }),

  tgMetrics: (ca: string) => {
    const url = new URL(API_ENDPOINTS.tgMetrics);
    url.searchParams.append('ca', ca);
    return apiClient<TGMetricsResponse>(url.toString());
  },

  // Repo analyzer
  getRepoAnalyzerData: (url: string) =>
    apiClient<RepoAnalyzerResponse>(API_ENDPOINTS.repoAnalyzer, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${process.env.REPO_ANALYZER_TOKEN}`,
      },
      body: JSON.stringify({
        token: process.env.REPO_ANALYZER_TOKEN,
        type: 'Bearer',
        expiresIn: 2592000,
        input: url,
      }),
    }),

  // Trench radar
  getTrenchRadarInfo: (ca: string) =>
    apiClient<TrenchRadarResponse>(API_ENDPOINTS.trenchRadar, {
      method: 'POST',
      body: JSON.stringify({ ca }),
    }),

  // User Management
  createUser: async (data: CreateUserRequest): Promise<UserResponse> => {
    return apiClient<UserResponse>(API_ENDPOINTS.user, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getUser: async (userId: string): Promise<UserResponse> => {
    return apiClient<UserResponse>(`${API_ENDPOINTS.user}?user_id=${userId}`, {
      method: 'GET',
    });
  },

  updateUser: async (userId: string, name: string): Promise<UserResponse> => {
    return apiClient<UserResponse>(API_ENDPOINTS.user, {
      method: 'PUT',
      body: JSON.stringify({ user_id: userId, name }),
    });
  },

  // View Management
  createView: async (data: CreateViewRequest): Promise<void> => {
    return apiClient<void>(API_ENDPOINTS.createView, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getViewHistory: async (userId: string, limit: number = 5): Promise<ViewHistoryResponse> => {
    const url = new URL(API_ENDPOINTS.viewHistory);
    url.searchParams.append('user_id', userId);
    url.searchParams.append('limit', limit.toString());
    return apiClient<ViewHistoryResponse>(url.toString());
  },

  // Credit APIs
  grantCredits: async (data: GrantCreditRequest): Promise<GrantCreditResponse> => {
    return apiClient<GrantCreditResponse>(API_ENDPOINTS.grantCredits, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  getCreditBalance: async (userId: string): Promise<CreditBalanceResponse> => {
    const url = `${API_ENDPOINTS.creditBalance}?user_id=${userId}`;
    return apiClient<CreditBalanceResponse>(url, { method: 'GET' });
  },

  grantCreditsSecure: async (data: GrantCreditRequest): Promise<GrantCreditResponse> => {
    return apiClient<GrantCreditResponse>('/api/credits/grant', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // NextJS API routes
  solanaSendTransaction: async (
    data: SolanaSendTransactionRequest,
  ): Promise<SolanaSendTransactionResponse> => {
    return apiClient<SolanaSendTransactionResponse>('/api/solana/send-transaction', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  solanaCreatePaymentTx: async (
    data: SolanaCreatePaymentTxRequest,
  ): Promise<SolanaCreatePaymentTxResponse> => {
    return apiClient<SolanaCreatePaymentTxResponse>('/api/solana/create-payment-tx', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },

  // Add other API methods here...
};
